import { NGHeading } from '@NGComponents/NGHeading/NGHeading';
import { EIcon } from '@NGComponents/NGIcon/NGIcon.enum';
import NGText from '@NGComponents/NGText';
import { ETextDecoration, ETextType } from '@NGComponents/NGText/NGText.enum';
import { EHTMLDisplay, EOverflow, ESize, ETextColor } from '@NGUtils/enums';
import NeutralButton from '@components/buttons/NeutralButton';
import { Collapse } from '@components/common/Collapse/Collapse';
import StrapiRichText from '@components/common/StrapiRichText';
import { EStrapiRichText } from '@components/common/StrapiRichText/StrapiRichText.enum';
import { IStrapiRichText } from '@components/common/StrapiRichText/StrapiRichText.type';
import Flex from '@layouts/Flex';
import { EFlex } from '@layouts/Flex/Flex.enum';
import useRemoveToast from '@store/toastsStore/hooks/useRemoveToast';
import Image from 'next/image';
import { FC, useMemo, useReducer } from 'react';
import { CSSTransition, SwitchTransition, TransitionGroup } from 'react-transition-group';
import { DEFAULT_TRANSITION_TIMEOUT } from 'src/utils/variables';
import {
	AnimatedArrowEx,
	FlexEx,
	FlexToastContentContainer,
	NGIconButtonEx,
	NGTextEx,
	StyledShieldContainer,
	StyledToastContainer
} from './Toast.styles';
import { IToast } from './Toast.type';
import { SHIELDS_SRC } from './Toast.variables';

const Toast: FC<IToast> = ({ uuid, variant, title, message, details, nonce }) => {
	const [showDetails, toggleShowDetails] = useReducer((v) => !v, false);
	const { removeToast } = useRemoveToast();

	const richTextMessage = useMemo(() => {
		if (typeof message !== 'string') return message;

		const newRichText = {
			id: 'toast-message',
			type: EStrapiRichText.paragraph,
			data: {
				text: message
			}
		};

		return [newRichText];
	}, [message]);

	const handleRemoveToast = () => removeToast(uuid);

	const handleClick = (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation();

	return (
		<SwitchTransition>
			<CSSTransition key={nonce ?? 'unset'} classNames='blink' timeout={DEFAULT_TRANSITION_TIMEOUT['300']}>
				<StyledToastContainer data-cy={`${variant}-toast`}>
					<FlexEx onClick={handleClick} marginBottom={ESize.xs}>
						<StyledShieldContainer>
							<Image quality={100} src={SHIELDS_SRC[variant]} alt={`${variant} toast shield`} layout='fill' />
						</StyledShieldContainer>

						<FlexToastContentContainer
							direction={EFlex.column}
							horizontal={EFlex.center}
							minHeight={'65px'}
							width='100%'
							paddingTop={ESize['3xs']}
							paddingRight={ESize['xs']}
							paddingBottom={ESize['2xs']}
							paddingLeft={ESize['xl']}
							overflow={EOverflow.hidden}
							variant={variant}
						>
							<NGIconButtonEx
								data-cy={`close-${variant}-toast`}
								onClick={handleRemoveToast}
								startIcon={EIcon.cross}
								size={ESize['2xs']}
							/>

							<NGHeading type={ETextType.h6}>{title}</NGHeading>

							{richTextMessage && (
								<Flex direction={EFlex.column} marginTop={ESize['5xs']}>
									{richTextMessage?.map(
										(
											{
												id,
												type,
												data,
												paragraphPaddingTop,
												paragraphPaddingRight,
												paragraphPaddingBottom,
												paragraphPaddingLeft
											}: IStrapiRichText,
											i
										) => (
											<StrapiRichText
												key={id}
												isFirst={i === 0}
												type={type}
												data={data}
												paragraphSize={ESize.s}
												paragraphPaddingTop={paragraphPaddingTop || 0}
												paragraphPaddingRight={paragraphPaddingRight || 0}
												paragraphPaddingBottom={paragraphPaddingBottom || 0}
												paragraphPaddingLeft={paragraphPaddingLeft || 0}
											/>
										)
									)}
								</Flex>
							)}

							{details && (
								<>
									<NeutralButton
										display={EHTMLDisplay.flex}
										onClick={toggleShowDetails}
										padding={ESize.unset}
										marginTop={ESize['5xs']}
										minHeight={'14px'}
										width='100%'
									>
										<TransitionGroup component={null}>
											<CSSTransition
												key={showDetails ? 'showDetails' : 'hideDetails'}
												classNames='fade'
												timeout={DEFAULT_TRANSITION_TIMEOUT['200']}
											>
												{showDetails ? (
													<NGTextEx
														size={ESize.s}
														textColor={ETextColor.gold}
														textDecoration={ETextDecoration.underline}
													>
														Hide details
													</NGTextEx>
												) : (
													<NGTextEx
														size={ESize.s}
														textColor={ETextColor.gold}
														textDecoration={ETextDecoration.underline}
													>
														Show details
													</NGTextEx>
												)}
											</CSSTransition>
										</TransitionGroup>

										<AnimatedArrowEx isActive={showDetails} width={11} height={8} />
									</NeutralButton>

									<Collapse isOpen={showDetails} width='100%'>
										<NGText size={ESize.s} paddingTop={ESize['4xs']} width='100%'>
											{details}
										</NGText>
									</Collapse>
								</>
							)}
						</FlexToastContentContainer>
					</FlexEx>
				</StyledToastContainer>
			</CSSTransition>
		</SwitchTransition>
	);
};

export { Toast };
